import { useEffect, useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import BotMessage from 'components/modals/supportChatModal/BotMessage'
import {
    getSupportBotConversation,
    ISupportBotTurn,
} from 'services/supportBot'
import { askSupportBotStreamed } from 'services/supportBot/stream'

interface IProps {
    show: boolean
    onHide: () => void
    /** Opens the ordinary human-support options (call / Eitaa / Telegram). */
    onWantsHuman: () => void
}

const GREETING =
    'سلام! من پشتیبان هوشمند همیان هستم. می‌توانید دربارهٔ صندوق، وام، اقساط، ' +
    'سرمایه و پرداخت‌هایتان از من بپرسید.'

const SUGGESTIONS = [
    'سرمایه‌ام در صندوق‌ها چقدر است؟',
    'اقساط پرداخت‌نشده‌ام کدام‌ها هستند؟',
    'کارمزد همیان چقدر است؟',
]

/**
 * Everything a failed request is allowed to say in an event.
 *
 * A closed enum and the status, never the server's own error text — this
 * endpoint's messages can quote the question back, and the question is the one
 * thing that must never reach analytics.
 */
const describeFailure = (error: any) => {
    const status = error?.response?.status ?? null
    const kind =
        error?.code === 'ECONNABORTED'
            ? 'timeout'
            : !status
            ? 'network'
            : status === 400
            ? 'http_400'
            : status === 503
            ? 'http_503'
            : status >= 500
            ? 'http_5xx'
            : 'unknown'
    return { error_kind: kind, status }
}

const SupportChatModal = ({ show, onHide, onWantsHuman }: IProps) => {
    const [turns, setTurns] = useState<ISupportBotTurn[]>([])
    const [draft, setDraft] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [didFail, setDidFail] = useState(false)
    const [wasEscalated, setWasEscalated] = useState(false)
    const [humanNotified, setHumanNotified] = useState(false)
    // The server owns the thread now. Holding its id here is what lets a
    // follow-up question continue the same conversation, and what stops the
    // member losing their context the moment they follow one of the bot's
    // links — which is the whole point of the bot giving links at all.
    const [conversationId, setConversationId] = useState<number | null>(null)
    const [isRestoring, setIsRestoring] = useState(false)
    /**
     * The answer as far as it has arrived, before the server's final word.
     *
     * Held apart from `turns` rather than appended to it, because it is not a
     * turn yet: the server may withdraw it (`onReset`), and what finally goes
     * into the transcript is `data.answer` — the authoritative text — not the
     * concatenation of what was shown. Keeping the two separate means the
     * conversation this component remembers is always the real one.
     */
    const [streamingText, setStreamingText] = useState('')
    const bottomRef = useRef<HTMLDivElement>(null)
    const openedAtRef = useRef(0)
    const askedCountRef = useRef(0)
    const lastOutcomeRef = useRef('none')

    // The answer takes several seconds, so the newest bubble is usually below
    // the fold by the time it lands.
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [turns, isLoading, streamingText])

    // What the close event reports is per-visit, and the component stays
    // mounted between visits, so the counters start over on the way in.
    useEffect(() => {
        if (!show) return
        openedAtRef.current = Date.now()
        askedCountRef.current = 0
        lastOutcomeRef.current = 'none'
    }, [show])

    // Put the member back in the conversation they were already having.
    //
    // The chat is a modal and its turns are component state, so anything that
    // closes it used to end the conversation. That was survivable while the
    // bot answered in prose; now that a good answer ends in a link the member
    // is meant to tap, the successful answers are precisely the ones that
    // destroyed the thread. The server keeps it, so ask for it.
    //
    // Only when there is nothing on screen: a restore must never overwrite a
    // question the member is in the middle of.
    useEffect(() => {
        if (!show || turns.length > 0 || isRestoring) return
        let cancelled = false
        setIsRestoring(true)
        getSupportBotConversation()
            .then((conversation) => {
                if (cancelled || !conversation?.turns?.length) return
                setTurns(conversation.turns)
                setConversationId(conversation.conversation_id)
                posthog.capture('support_bot_conversation_restored', {
                    turns_count: conversation.turns.length,
                })
            })
            // A backend without this endpoint, or an offline phone, means no
            // restore — never a broken chat. Starting fresh is what this
            // component did before the endpoint existed.
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setIsRestoring(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    const send = async (text: string, isSuggestion = false) => {
        const question = text.trim()
        if (!question || isLoading) return

        setDraft('')
        setDidFail(false)
        setWasEscalated(false)
        setHumanNotified(false)
        // History is taken from the state we had BEFORE appending, so the new
        // question is not sent twice.
        const history = turns.slice(-6)
        setTurns((previous) => [
            ...previous,
            { role: 'user', content: question },
        ])
        setIsLoading(true)

        const turnIndex = askedCountRef.current
        askedCountRef.current += 1
        posthog.capture('support_bot_question_asked', {
            turn_index: turnIndex,
            question_length: question.length,
            is_suggestion: isSuggestion,
            is_followup: turns.length > 0,
            history_turns: history.length,
        })

        const startedAt = Date.now()
        setStreamingText('')
        // When the first character actually reached the member, as opposed to
        // when the whole answer did. This is the number streaming exists to
        // move, and the old `answer_ms` cannot show it.
        let firstTokenAt = 0
        try {
            const data = await askSupportBotStreamed(
                { question, history, conversationId },
                {
                    onDelta: (text) => {
                        if (!firstTokenAt) firstTokenAt = Date.now()
                        setStreamingText((previous) => previous + text)
                    },
                    // The server withdrew what it was showing — that text was
                    // a tool preamble or a failed round, not the answer.
                    onReset: () => setStreamingText(''),
                }
            )
            setTurns((previous) => [
                ...previous,
                { role: 'assistant', content: data.answer },
            ])
            // Cleared only now, and in the same commit as the turn above, so
            // the bubble never blinks empty between the two renders.
            setStreamingText('')
            setWasEscalated(data.escalated)
            // Only ever what the server reported. It sets this when the team
            // group was genuinely pinged, and the answer promises a callback
            // only in that case — the bot once made that promise with nothing
            // behind it and the member is still waiting.
            setHumanNotified(Boolean(data.human_notified))
            if (data.conversation_id) setConversationId(data.conversation_id)
            // The server's own failures — spent time budget, provider error,
            // empty completion, too many tool rounds — all arrive as a 200 with
            // a canned reply, so a resolved promise is not evidence of an
            // answer and `outcome` is what tells them apart. Read defensively:
            // a backend older than this client sends neither field.
            const outcome = data.outcome ?? 'unknown'
            lastOutcomeRef.current = outcome
            posthog.capture('support_bot_answered', {
                escalated: data.escalated,
                tools_used: data.tools_used,
                tool_calls_count: data.tools_used?.length ?? 0,
                answer_ms: Date.now() - startedAt,
                // Time to the FIRST character. 0 means nothing streamed and
                // the member watched a spinner for the whole `answer_ms` —
                // which is what a buffered fallback looks like in the funnel.
                first_token_ms: firstTokenAt ? firstTokenAt - startedAt : 0,
                streamed: firstTokenAt > 0,
                answer_length: data.answer?.length ?? 0,
                turn_index: turnIndex,
                outcome,
                human_notified: Boolean(data.human_notified),
                ...(data.escalation_reason
                    ? { escalation_reason: data.escalation_reason }
                    : {}),
            })
        } catch (error) {
            setStreamingText('')
            setDidFail(true)
            lastOutcomeRef.current = 'failed'
            posthog.capture('support_bot_failed', {
                ...describeFailure(error),
                answer_ms: Date.now() - startedAt,
                turn_index: turnIndex,
            })
        } finally {
            setIsLoading(false)
        }
    }

    /**
     * Close the chat WITHOUT throwing the conversation away.
     *
     * `setTurns([])` used to live here, which made closing the modal the same
     * thing as ending the conversation. That was already unkind — a member who
     * closed it by accident retyped everything — and it became a real bug once
     * the bot started answering with links: following one closes the modal, so
     * the answer that helped most was the one that erased the context.
     *
     * The turns stay in state for a quick reopen, and the server's copy backs
     * that up across a remount or a reload. `keepDraft` is for the link case:
     * the member is going somewhere and coming back, not finishing.
     */
    const close = (keepDraft = false) => {
        posthog.capture('support_bot_closed', {
            turns_count: turns.length,
            asked_count: askedCountRef.current,
            last_outcome: lastOutcomeRef.current,
            was_escalated: wasEscalated,
            seconds_open: openedAtRef.current
                ? Math.round((Date.now() - openedAtRef.current) / 1000)
                : 0,
        })
        if (!keepDraft) setDraft('')
        setDidFail(false)
        onHide()
    }

    /** The member tapped a link in an answer: step aside and let them go. */
    const followLink = () => {
        posthog.capture('support_bot_link_followed', {
            turn_index: Math.max(askedCountRef.current - 1, 0),
        })
        close(true)
    }

    return (
        <Modal show={show} onHide={() => close()} centered scrollable>
            <div className="flex flex-col text-xs" style={{ height: '70vh' }}>
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <span className="font-bold">پشتیبان هوشمند</span>
                    <span className="text-[10px] text-gray-400">
                        نسخهٔ آزمایشی
                    </span>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                    <div className="bg-gray-100 rounded-lg p-3 mb-3 leading-6">
                        {GREETING}
                    </div>

                    {turns.length === 0 && !isLoading && (
                        <div className="flex flex-col gap-2 mb-3">
                            {SUGGESTIONS.map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => send(item, true)}
                                    className="text-right border border-green-1 text-green-1 rounded-lg px-3 py-2"
                                >
                                    {item}
                                </button>
                            ))}
                        </div>
                    )}

                    {turns.map((turn, index) => (
                        <div
                            key={index}
                            className={
                                turn.role === 'user'
                                    ? 'flex justify-start mb-3'
                                    : 'flex justify-end mb-3'
                            }
                        >
                            <div
                                className={
                                    turn.role === 'user'
                                        ? 'bg-green-1 text-white rounded-lg p-3 max-w-[85%] leading-6'
                                        : 'bg-gray-100 rounded-lg p-3 max-w-[85%] leading-6'
                                }
                                style={
                                    turn.role === 'user'
                                        ? { whiteSpace: 'pre-wrap' }
                                        : undefined
                                }
                            >
                                {turn.role === 'user' ? (
                                    turn.content
                                ) : (
                                    // An answer's links are the answer now —
                                    // the bot hands over a route instead of
                                    // describing one — so they have to be
                                    // pressable, not just visible.
                                    <BotMessage
                                        text={turn.content}
                                        onNavigate={followLink}
                                    />
                                )}
                            </div>
                        </div>
                    ))}

                    {/* The answer as it is being written. Plain text, not
                        `BotMessage`: a link is only pressable once it is
                        whole, and half a URL rendered as an anchor is a tap
                        that goes nowhere. The finished turn below renders
                        through `BotMessage` as it always did. */}
                    {isLoading && streamingText && (
                        <div className="flex justify-end mb-3">
                            <div
                                className="bg-gray-100 rounded-lg p-3 max-w-[85%] leading-6"
                                style={{ whiteSpace: 'pre-wrap' }}
                            >
                                {streamingText}
                            </div>
                        </div>
                    )}

                    {/* Dots only while there is nothing to read yet. Once text
                        is arriving, the text IS the progress indicator. */}
                    {isLoading && !streamingText && (
                        <div className="flex justify-end mb-3">
                            <div className="bg-gray-100 rounded-lg px-4 py-3 text-green-1">
                                <DotsLoading />
                            </div>
                        </div>
                    )}

                    {didFail && (
                        <div className="text-red-500 text-center my-2 leading-6">
                            پاسخی دریافت نشد. لطفاً دوباره تلاش کنید یا با
                            پشتیبانی تماس بگیرید.
                        </div>
                    )}

                    {/* The server already told the team group; say so, and
                        do not also offer to "connect" them as if it had not.
                        Promising a callback that nobody received is the exact
                        failure this whole path was built to end. */}
                    {humanNotified && (
                        <div className="text-center my-2 leading-6 text-green-1">
                            به همکاران پشتیبانی اطلاع دادیم و با شما تماس
                            می‌گیرند.
                        </div>
                    )}

                    {/* Whenever the backend decided a human is needed, make
                        reaching one a single tap rather than a instruction. */}
                    {(wasEscalated || didFail) && (
                        <button
                            type="button"
                            onClick={() => {
                                // Captured before close(), which resets
                                // everything this event is made of.
                                posthog.capture(
                                    'support_bot_escalation_accepted',
                                    {
                                        reason: wasEscalated
                                            ? 'escalated'
                                            : 'failed',
                                        turn_index: Math.max(
                                            askedCountRef.current - 1,
                                            0
                                        ),
                                    }
                                )
                                close()
                                onWantsHuman()
                            }}
                            className="w-full bg-green-1 text-white rounded-lg py-2 my-2"
                        >
                            تماس با پشتیبانی
                        </button>
                    )}

                    <div ref={bottomRef} />
                </div>

                <div className="border-t px-3 py-2">
                    <form
                        onSubmit={(event) => {
                            event.preventDefault()
                            send(draft)
                        }}
                        className="flex items-center gap-2"
                    >
                        <input
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            placeholder="سوالتان را بنویسید…"
                            maxLength={1000}
                            disabled={isLoading}
                            className="flex-1 border rounded-lg px-3 py-2 outline-none"
                        />
                        <button
                            type="submit"
                            disabled={isLoading || !draft.trim()}
                            className="bg-green-1 text-white rounded-lg px-4 py-2 disabled:opacity-40"
                        >
                            ارسال
                        </button>
                    </form>
                    <p
                        className="text-[10px] text-gray-400 text-center mt-2 mb-0 cursor-pointer"
                        onClick={() => close()}
                    >
                        بستن
                    </p>
                </div>
            </div>
        </Modal>
    )
}

export default SupportChatModal
